/**
 * Browser client for the WooCommerce Store API.
 *
 * This is the one part of the site that talks to WordPress at runtime. The rest
 * is prerendered from local files; cart and checkout cannot be, because totals,
 * stock and payment are WooCommerce's to decide.
 *
 * Requests go to the **same origin** — Cloudflare routes `/wp-json/*` to the
 * WordPress origin. Same-origin matters: the Store API's session lives in a
 * cookie plus a `Cart-Token`, and a cross-origin call would drop both.
 *
 * Two headers carry the session:
 *
 * - `Cart-Token` — a JWT identifying the cart. Returned by every response and
 *   echoed back on the next request. Persisted so a reload keeps the cart.
 * - `Nonce` — CSRF protection, required on mutations. Rotates; always use the
 *   most recent one the server returned.
 *
 * Prices in every response are **minor units** (`"63000"` = ฿630.00).
 */

const BASE = '/wp-json/wc/store/v1';
const TOKEN_KEY = 'dp_cart_token';

export interface StoreApiError extends Error {
	code?: string;
	status?: number;
	/** Field-level messages, when WooCommerce rejects specific inputs. */
	details?: Record<string, { message: string }>;
}

/** Latest nonce, kept in memory — it rotates and must not go stale. */
let nonce: string | null = null;

const browser = typeof window !== 'undefined';

function readToken(): string | null {
	if (!browser) return null;
	try {
		return window.localStorage.getItem(TOKEN_KEY);
	} catch {
		return null; // private browsing, storage disabled
	}
}

function writeToken(value: string): void {
	if (!browser) return;
	try {
		window.localStorage.setItem(TOKEN_KEY, value);
	} catch {
		/* non-fatal: the cart falls back to the session cookie */
	}
}

export function clearCartToken(): void {
	if (!browser) return;
	try {
		window.localStorage.removeItem(TOKEN_KEY);
	} catch {
		/* ignore */
	}
	nonce = null;
}

async function toError(response: Response): Promise<StoreApiError> {
	let body: Record<string, unknown> = {};
	try {
		body = await response.json();
	} catch {
		/* non-JSON error body */
	}

	const error = new Error(
		(body.message as string) ?? `Request failed (${response.status})`
	) as StoreApiError;
	error.code = body.code as string | undefined;
	error.status = response.status;
	error.details = (body.data as { params?: Record<string, { message: string }> })?.params;
	return error;
}

/**
 * Ensures a nonce is in hand before a mutation.
 *
 * The nonce only arrives on a response, so the very first action in a session —
 * typically add-to-cart — has nothing to send and WooCommerce rejects it with
 * "Missing the Nonce header". A cheap `GET /cart` primes it.
 */
async function ensureNonce(): Promise<void> {
	if (nonce) return;
	try {
		await send<Cart>('/cart', {});
	} catch {
		// If priming fails the mutation will surface the real error itself.
	}
}

/**
 * Nonces rotate and expire, so a rejected one is worth exactly one retry with a
 * fresh value. Anything beyond that is a real failure.
 */
function isNonceFailure(error: StoreApiError): boolean {
	if (error.status !== 401 && error.status !== 403) return false;
	const text = `${error.code ?? ''} ${error.message}`.toLowerCase();
	return text.includes('nonce');
}

/**
 * Store API endpoints on which WooCommerce reports the payment methods truthfully.
 *
 * The Omise plugin does not compute availability on every request. Its
 * `Omise_Capability::retrieve()` bails out unless `isFromCheckoutPage()` says so,
 * and that function allowlists REST routes by suffix —
 * `['checkout', 'batch', 'cart', 'cart/select-shipping-rate']`
 * (`includes/class-omise-capability.php`). On anything else it returns `null`,
 * every Omise gateway's `is_available()` returns false, and WooCommerce answers
 * with `payment_methods: ['cod']`.
 *
 * `cart/update-customer` is not on that list. So filling in the address — the one
 * action guaranteed to happen on the checkout page — made card, PromptPay and
 * mobile banking vanish and left cash on delivery as the only option. The classic
 * WooCommerce checkout never hit this because it recalculates through
 * `wc-ajax=update_order_review`, which the plugin does allowlist; a Store API
 * checkout is what exposes it.
 *
 * The methods are account-level capability and do not depend on the cart, which is
 * what makes carrying the last authoritative list forward correct rather than a
 * guess: an immediate `GET /cart` after a degraded `update-customer` returns all
 * four again, for the same cart.
 */
const CAPABILITY_ENDPOINTS = ['/cart', '/cart/select-shipping-rate', '/checkout', '/batch'];

/** The last list from an endpoint that was in a position to report it. */
let knownPaymentMethods: string[] | null = null;

function keepPaymentMethods<T>(path: string, result: T): T {
	const cart = result as { payment_methods?: string[] } | null;
	if (!cart || !Array.isArray(cart.payment_methods)) return result;

	if (CAPABILITY_ENDPOINTS.includes(path)) {
		knownPaymentMethods = cart.payment_methods;
	} else if (knownPaymentMethods) {
		cart.payment_methods = knownPaymentMethods;
	}

	return result;
}

async function request<T>(
	path: string,
	options: { method?: string; body?: unknown } = {}
): Promise<T> {
	const isMutation = (options.method ?? 'GET') !== 'GET';
	if (isMutation) await ensureNonce();

	try {
		return keepPaymentMethods(path, await send<T>(path, options));
	} catch (error) {
		const failure = error as StoreApiError;
		if (isMutation && isNonceFailure(failure)) {
			nonce = null;
			await ensureNonce();
			return keepPaymentMethods(path, await send<T>(path, options));
		}
		throw error;
	}
}

async function send<T>(
	path: string,
	{ method = 'GET', body }: { method?: string; body?: unknown } = {}
): Promise<T> {
	const headers: Record<string, string> = { Accept: 'application/json' };
	if (body !== undefined) headers['Content-Type'] = 'application/json';

	const token = readToken();
	if (token) headers['Cart-Token'] = token;
	if (nonce) headers['Nonce'] = nonce;

	const response = await fetch(`${BASE}${path}`, {
		method,
		headers,
		// Send the WooCommerce session cookie; without it the cart is anonymous
		// on every request and totals reset.
		credentials: 'same-origin',
		body: body === undefined ? undefined : JSON.stringify(body)
	});

	// Capture rotated credentials even on failure — the next attempt needs them.
	const freshToken = response.headers.get('cart-token');
	if (freshToken) writeToken(freshToken);
	const freshNonce = response.headers.get('nonce');
	if (freshNonce) nonce = freshNonce;

	if (!response.ok) throw await toError(response);
	return (await response.json()) as T;
}

/* ---------- types ---------- */

export interface CartMoney {
	currency_code: string;
	currency_minor_unit: number;
	currency_symbol: string;
	currency_prefix: string;
	currency_suffix: string;
	currency_thousand_separator: string;
	currency_decimal_separator: string;
}

export interface CartItem {
	key: string;
	id: number;
	quantity: number;
	name: string;
	short_description: string;
	permalink: string;
	images: { src: string; alt: string }[];
	variation: { attribute: string; value: string }[];
	prices: CartMoney & { price: string; regular_price: string; sale_price: string };
	totals: CartMoney & { line_total: string; line_subtotal: string };
	quantity_limits?: { minimum: number; maximum: number; multiple_of: number };
}

export interface ShippingRate {
	rate_id: string;
	name: string;
	price: string;
	selected: boolean;
}

export interface Cart {
	items: CartItem[];
	items_count: number;
	needs_shipping: boolean;
	needs_payment: boolean;
	coupons: { code: string; totals: { total_discount: string } }[];
	shipping_rates: { package_id: number; shipping_rates: ShippingRate[] }[];
	billing_address: Record<string, string>;
	shipping_address: Record<string, string>;
	payment_methods: string[];
	totals: CartMoney & {
		total_items: string;
		total_price: string;
		total_tax: string;
		total_shipping: string;
		total_discount: string;
	};
	errors: { code: string; message: string }[];
}

export interface CheckoutResponse {
	order_id: number;
	status: string;
	order_key: string;
	payment_result: {
		payment_status: 'success' | 'failure' | 'pending' | 'error';
		payment_details: { key: string; value: string }[];
		redirect_url: string;
	};
}

/* ---------- operations ---------- */

export const getCart = () => request<Cart>('/cart');

export const addItem = (id: number, quantity = 1) =>
	request<Cart>('/cart/add-item', { method: 'POST', body: { id, quantity } });

export const updateItem = (key: string, quantity: number) =>
	request<Cart>('/cart/update-item', { method: 'POST', body: { key, quantity } });

export const removeItem = (key: string) =>
	request<Cart>('/cart/remove-item', { method: 'POST', body: { key } });

export const applyCoupon = (code: string) =>
	request<Cart>('/cart/apply-coupon', { method: 'POST', body: { code } });

export const removeCoupon = (code: string) =>
	request<Cart>('/cart/remove-coupon', { method: 'POST', body: { code } });

export const selectShippingRate = (packageId: number, rateId: string) =>
	request<Cart>('/cart/select-shipping-rate', {
		method: 'POST',
		body: { package_id: packageId, rate_id: rateId }
	});

/** Recalculates tax and shipping for an address, before the order exists. */
export const updateCustomer = (body: {
	billing_address?: Record<string, string>;
	shipping_address?: Record<string, string>;
}) => request<Cart>('/cart/update-customer', { method: 'POST', body });

/**
 * Places the order.
 *
 * WooCommerce computes the total server-side and the Omise plugin creates the
 * charge — no price is ever sent from the browser, so there is nothing to
 * tamper with.
 */
export const submitCheckout = (body: {
	billing_address: Record<string, string>;
	shipping_address: Record<string, string>;
	payment_method: string;
	payment_data?: { key: string; value: string }[];
	customer_note?: string;
}) => request<CheckoutResponse>('/checkout', { method: 'POST', body });

/** Formats a Store API minor-unit amount, e.g. `"63000"` → `฿630`. */
export function formatMoney(amount: string | number, money: CartMoney): string {
	const minor = typeof amount === 'string' ? Number.parseInt(amount, 10) : amount;
	if (!Number.isFinite(minor)) return '';

	const divisor = 10 ** money.currency_minor_unit;
	const major = minor / divisor;
	const hasFraction = minor % divisor !== 0;

	const formatted = major.toLocaleString('en-US', {
		minimumFractionDigits: hasFraction ? money.currency_minor_unit : 0,
		maximumFractionDigits: money.currency_minor_unit
	});

	return `${money.currency_prefix}${formatted}${money.currency_suffix}`;
}
